import React from "react";
import { Link } from "react-router-dom";
import { MessageSquare } from "lucide-react";

const EFFECTIVE = "June 2026";

export default function MessagingPolicy() {
  return (
    <div className="max-w-2xl mx-auto animate-slide-up py-4" data-testid="messaging-policy-page">
      <div className="flex items-center gap-2 text-race-red mb-2">
        <MessageSquare size={16} />
        <span className="overline">SMS / Messaging Policy</span>
      </div>
      <h1 className="font-heading text-4xl sm:text-5xl tracking-tight">Text Message Terms & Consent</h1>
      <p className="text-race-sub mt-2 text-sm">Effective {EFFECTIVE} · Text Racing (sanctioned by IBRA)</p>

      {/* Public example of the exact consent form shown in Profile → Race Reminders
          (the live form is behind account login, so this mirrors it for review). */}
      <div className="mt-6 bg-race-surface border border-race-line rounded-lg p-5" data-testid="sms-optin-example">
        <div className="overline text-race-sub mb-3">SMS sign-up form (as shown in Profile → Race Reminders)</div>
        <label className="overline block mb-1">Mobile Phone Number *</label>
        <div className="w-full bg-race-bg border border-race-line px-3 py-2.5 rounded-sm text-race-muted">(555) 123-4567</div>
        <label className="flex items-start gap-3 mt-4 text-sm text-race-txt">
          <span className="mt-0.5 w-5 h-5 rounded border border-race-line inline-block shrink-0" aria-hidden />
          <span>Yes, I agree to receive automated SMS race reminders from Text Racing at the number above. Consent is not a condition of entering any contest.</span>
        </label>
        <p className="text-xs text-race-sub mt-3"><b>Message frequency:</b> about 1 message per race you register for.</p>
        <p className="text-xs text-race-sub"><b>Standard rates:</b> Message &amp; data rates may apply.</p>
        <p className="text-xs text-race-sub"><b>Help &amp; Stop:</b> Reply HELP for help, STOP to cancel anytime.</p>
        <div className="mt-3 text-xs">
          <Link to="/terms" className="text-race-red">Terms of Service</Link>
          <span className="text-race-sub"> · </span>
          <Link to="/privacy" className="text-race-red">Privacy Policy</Link>
        </div>
        <div className="mt-4 bg-race-red text-race-bg text-center font-heading text-xl py-2.5 rounded-sm">Yes, sign me up!</div>
        <p className="text-[11px] text-race-muted mt-2">Note: the consent box is never pre-selected; the racer must actively check it.</p>
      </div>

      <div className="mt-6 space-y-6 text-race-txt leading-relaxed text-sm sm:text-base">
        <section>
          <h2 className="font-heading text-2xl mb-2">What messages we send</h2>
          <p>
            Text Racing sends <b>race reminder</b> text messages to members who opt in. These are
            transactional messages that alert you that a contest you registered for is starting soon,
            with a direct link to the Start-Line. Example:
          </p>
          <blockquote className="mt-3 border-l-2 border-race-red pl-4 text-race-sub">
            “Text Racing: your race starts in 5 min. Join: https://textracing.com/lobby/12345
            Msg &amp; data rates may apply. Reply HELP for help, STOP to unsubscribe.”
          </blockquote>
        </section>

        <section>
          <h2 className="font-heading text-2xl mb-2">How you opt in (consent)</h2>
          <p>
            You provide express written consent by going to <b>Profile → Race Reminders</b>, entering
            your mobile number, and checking the box that reads <i>“I agree to receive SMS race
            reminders from Text Racing.”</i> Consent is not a condition of purchase or of entering any
            contest. You may also opt in verbally with a Text Racing representative where offered.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl mb-2">Message frequency</h2>
          <p>
            Frequency varies and depends on how many races you register for - typically one reminder
            per registered race (sent 3, 5, or 10 minutes before start, per your preference).
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl mb-2">Opt out &amp; help</h2>
          <p>
            Reply <b>STOP</b> to any message to unsubscribe at any time; you will receive a
            confirmation and no further texts. Reply <b>HELP</b> for assistance, or email
            <a className="text-race-red" href="mailto:support@textracing.com"> support@textracing.com</a>.
            You can also turn reminders off anytime in Profile → Race Reminders.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl mb-2">Cost</h2>
          <p><b>Message and data rates may apply.</b> Text Racing does not charge for reminder texts; your carrier’s standard rates apply.</p>
        </section>

        <section>
          <h2 className="font-heading text-2xl mb-2">Privacy</h2>
          <p>
            <b>We do not sell or share your opt-in information or phone number with third parties or
            affiliates for their marketing purposes.</b> Your number is used only to deliver the race
            reminders you requested. See our{" "}
            <Link className="text-race-red" to="/privacy">Privacy Policy</Link> and{" "}
            <Link className="text-race-red" to="/terms">Terms of Service</Link>.
          </p>
        </section>

        <section>
          <h2 className="font-heading text-2xl mb-2">Supported carriers</h2>
          <p className="text-race-sub">
            Carriers are not liable for delayed or undelivered messages. Delivery is subject to
            carrier availability.
          </p>
        </section>
      </div>

      <div className="mt-8 flex gap-3 flex-wrap">
        <Link to="/privacy" data-testid="link-privacy" className="text-sm border border-race-line px-4 py-2 rounded-sm hover:border-race-red transition-colors">Privacy Policy</Link>
        <Link to="/terms" data-testid="link-terms" className="text-sm border border-race-line px-4 py-2 rounded-sm hover:border-race-red transition-colors">Terms of Service</Link>
      </div>
    </div>
  );
}
